import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "../utils/axiosConfig";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  });

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [accountLinkRequired, setAccountLinkRequired] = useState(false);

const refreshUser = useCallback(async () => {
  if (!isAuthenticated) {
    setUser(null);
    setProfileLoaded(true);
    setProfileLoading(false);
    setProfileError(null);
    setAccountLinkRequired(false);
    localStorage.removeItem("user");
    return null;
  }

  try {
    setProfileLoading(true);
    setProfileError(null);

    await getAccessTokenSilently({
      authorizationParams: {
        audience: "https://someet-api",
      },
    });

    const { data } = await axios.get("/me");

    // /me zadziałało normalnie,
    // więc linkowanie nie jest wymagane.
    setAccountLinkRequired(false);

    setUser(data);
    localStorage.setItem("user", JSON.stringify(data));
    setProfileLoaded(true);

    return data;
  } catch (err) {
    console.error("refreshUser error:", err);

    const status = err?.response?.status;

    const errorMessage =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.response?.data?.error;

    if (
      status === 409 &&
      errorMessage === "ACCOUNT_LINK_REQUIRED"
    ) {
      setUser(null);
      localStorage.removeItem("user");

      setAccountLinkRequired(true);
      setProfileLoaded(true);

      throw err;
    }

    setProfileError(err);
    setProfileLoaded(true);
    throw err;
  } finally {
    setProfileLoading(false);
  }
}, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setUser(null);
      setProfileLoaded(true);
      setProfileLoading(false);
      setProfileError(null);
      setAccountLinkRequired(false);
      localStorage.removeItem("user");
      return;
    }

    refreshUser().catch(() => {});
  }, [isLoading, isAuthenticated, refreshUser]);

  const value = useMemo(
    () => ({
      user,
      setUser,
      refreshUser,
      profileLoading,
      profileLoaded,
      profileError,
      accountLinkRequired,
    }),
    [user, refreshUser, profileLoading, profileLoaded, profileError, accountLinkRequired]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used inside UserProvider");
  }
  return ctx;
}